/**
 * 날씨 아이콘
 * ---------------------------------------------------------------------------
 * WMO 날씨 코드를 그림으로 보여 준다. 목록에서 "맑음/흐림/비" 를 글자로 읽는
 * 것보다 한 달치를 훑을 때 훨씬 빠르다.
 *
 * 아이콘 선택은 화면이 한다
 *   서버는 코드만 내려보낸다(`weatherCode`). 아이콘 세트를 바꿀 때 서버를
 *   건드릴 이유가 없다.
 *
 * 코드가 없을 때
 *   사용자가 직접 고른 날씨는 코드가 없다. 그때는 글자로 되짚어 본다 —
 *   아이콘이 갑자기 사라지면 "어제는 있었는데" 가 된다.
 */

import { Tooltip } from 'antd';
import type { WeatherIcon as IconName } from '@eco/shared';
import { weatherIcon } from '@eco/shared';

/** 글자 → 아이콘. 사용자가 직접 고른 날씨에 쓴다. */
const LABEL_ICON: Array<[RegExp, IconName]> = [
  [/뇌우|천둥/, 'thunder'],
  [/눈/, 'snow'],
  [/소나기|비/, 'rain'],
  [/이슬비/, 'drizzle'],
  [/안개/, 'fog'],
  [/흐림/, 'cloudy'],
  [/구름/, 'partly'],
  [/맑음/, 'clear'],
];

/** 아이콘과 기본 설명. 글꼴에 의존하지 않도록 이모지를 쓴다. */
const GLYPH: Record<IconName, { glyph: string; label: string }> = {
  clear: { glyph: '☀️', label: '맑음' },
  partly: { glyph: '⛅', label: '구름조금' },
  cloudy: { glyph: '☁️', label: '흐림' },
  fog: { glyph: '🌫️', label: '안개' },
  drizzle: { glyph: '🌦️', label: '이슬비' },
  rain: { glyph: '🌧️', label: '비' },
  snow: { glyph: '❄️', label: '눈' },
  thunder: { glyph: '⛈️', label: '뇌우' },
};

export function resolveIcon(
  code: number | null | undefined,
  label: string | null | undefined,
): IconName | null {
  const byCode = weatherIcon(code);
  if (byCode) return byCode;
  if (!label) return null;
  return LABEL_ICON.find(([pattern]) => pattern.test(label))?.[1] ?? null;
}

export function WeatherIcon({
  code,
  label,
  size = 16,
  showLabel = false,
}: {
  code?: number | null;
  label?: string | null;
  size?: number;
  showLabel?: boolean;
}) {
  const name = resolveIcon(code, label);
  const text = label ?? (name ? GLYPH[name].label : null);

  if (!name) {
    // 아이콘을 못 고르면 글자라도 보여 준다. 빈 칸은 "날씨를 안 적었다" 와
    // 구분되지 않는다.
    return <span>{text ?? '-'}</span>;
  }

  return (
    <Tooltip title={text}>
      <span style={{ whiteSpace: 'nowrap' }}>
        <span style={{ fontSize: size, lineHeight: 1 }}>{GLYPH[name].glyph}</span>
        {showLabel && text && <span style={{ marginLeft: 6 }}>{text}</span>}
      </span>
    </Tooltip>
  );
}
